import { parse } from "@conform-to/zod";
import { invariant } from "@pexip-engage/utils";
import { MeetingTypeSchema, type PluginState } from "@pexip-engage-public/plugin-state";
import { type ActionFunctionArgs, json, redirect } from "@remix-run/node";
import { useActionData } from "@remix-run/react";
import type {
  Location,
  MeetingType,
  SchedulableListing,
  SubjectQuestion,
  WithAnswers,
  WithCalendarLinks,
  WithContact,
  WithCustomer,
  WithPossibilities,
} from "skedify-types";
import { z } from "zod";

import { AppErrorBoundary } from "~/components/AppErrorBoundary";
import { Confirmation } from "~/components/Confirmation";
import { PrimaryLink } from "~/components/Link";
import { resolveNewCustomerData } from "~/modules/customer/customer.action.server";
import {
  createServerStateManager,
  RequestConfig,
  ServerStateManager,
} from "~/modules/StateManager.server";
import { getPlaceDetailsById } from "~/services/google.server";
import { getInfraConfiguration } from "~/services/infra-configuration.server";
import { getOffice, getSubject } from "~/services/schedulingRules.server";
import { getListing, getListingId } from "~/services/sdk-api/listings.server";
import { getSubjectQuestions } from "~/services/sdk-api/subjectQuestions.server";
import { transformAppointmentDetails } from "~/utils/appointment.utils.server";
import { convertISOStringToDate, formatISOWithoutMs } from "~/utils/date";
import { parseSubjectQuestionsFormData } from "~/utils/form.utils";
import { setTimetableOverlappingSlotError } from "~/utils/timetable.utils";
import { errors } from "~/utils/validators.utils";
import { throwNotFoundError } from "~/utils/validators.utils.server";
import { zDate } from "~/utils/zod.utils";
import { APIErrorResponseSchema } from "~/utils/zodSchemas.utils";

// Currently we show the same error message for all errors. This map is created here
// to add different error messages in the future for different errors.
const TIMETABLE_NO_OVERLAP_ERROR = 42201;
const TIMETABLE_NO_AVAILABILITY_ERROR = 42202;
const TIMETABLE_CONFLICT_ERROR = 40901;

export const timetableErrors = [
  TIMETABLE_NO_OVERLAP_ERROR,
  TIMETABLE_NO_AVAILABILITY_ERROR,
  TIMETABLE_CONFLICT_ERROR,
];

type AppointmentIncludes = WithPossibilities &
  WithCustomer &
  WithAnswers &
  WithContact &
  WithCalendarLinks;

const schema = z.object({
  recaptcha: z.string(),
  metadata: z
    .string()
    .optional()
    .transform((val) => val && JSON.parse(val)),
});

export async function action(args: ActionFunctionArgs) {
  return args.context.measurer.time(
    "$enterprise_schedule_confirmation",
    async function scheduleConfirmationAction() {
      const formData = await args.request.formData();
      const submission = parse(formData, { schema }).value;
      invariant(submission, "Expected submission in confirmation");
      const stateManager = (await createServerStateManager(args)).setState((draft) => {
        draft.recaptcha = submission.recaptcha;
        draft.metadata = submission.metadata;
      });

      await stateManager.persist();

      stateManager.dispatchCAEvent({ type: "appointment-requested", payload: {} });

      const data = validateAppointmentFormData(stateManager.state);

      const questionsPromise = getSubjectQuestions(data.subjectId, stateManager.getRequestConfig());
      const { EXTERNAL_API_URL, SDK } = getInfraConfiguration(stateManager.enterprise);

      const appointmentData = await resolveAppointmentDetails({
        stateManager,
        data,
        questionsPromise,
      });

      const [appointment, office, subject, questions, listingId] = await Promise.all([
        SDK.appointments()
          .headers(stateManager.getRequestConfig().headers)
          .include<AppointmentIncludes>(
            SDK.include.contact.user,
            SDK.include.possibilities,
            // TODO doesnt work for now: https://skedify.atlassian.net/browse/SKED-9469
            // SDK.include.subject,
            // SDK.include.subject.subject_category,
            SDK.include.answers,
            SDK.include.customer,
            SDK.include.calendar_links,
          )
          //@ts-ignore
          .new(appointmentData)
          .then((res) => res.create())
          .then((appointment) => appointment.data)
          .catch((rawResponse) => handleTimetableErrorResponse(rawResponse)),
        getOffice(data.officeId, stateManager.getRequestConfig()),
        getSubject(data.subjectId, stateManager.getRequestConfig()),
        questionsPromise,
        getListingId({
          config: stateManager.getRequestConfig(),
          params: stateManager.state.config.listing,
        }),
      ]);

      if (!appointment) {
        await setTimetableOverlappingSlotError(stateManager).persist();

        const searchParams = new URLSearchParams();
        searchParams.set("startDate", convertISOStringToDate(data.timeSlot.start.toISOString()));

        return redirect(`../schedule/timetable?${searchParams}`);
      }

      const appointmentDetails = await transformAppointmentDetails({
        identity: "customer",
        appointment,
        listingId,
        subject,
        questions,
        office,
        API_URL: EXTERNAL_API_URL,
        isReschedulableAppointment: true,
      });

      stateManager.dispatchCAEvent({
        type: "appointment-confirmed",
        payload: { appointmentId: Number.parseInt(appointment.id) },
      });

      // Mark this session as final? block back button?
      const manager = stateManager.updateWithAppointment(appointmentDetails);
      await manager.persist();

      return json({ appointment: appointmentDetails }, { status: 201 });
    },
  );
}

export default function ScheduleAppointmentConfirmation() {
  const actionData = useActionData<typeof action>();

  if (!actionData) {
    return (
      <div className="flex justify-center p-md">
        <PrimaryLink to="../../plugin" reloadDocument>
          Start over
        </PrimaryLink>
      </div>
    );
  }

  return <Confirmation {...actionData} type="APPOINTMENT_CREATED" />;
}

export function ErrorBoundary() {
  return <AppErrorBoundary />;
}

const appointmentValidationSchema = z.object({
  subjectId: z.string(),
  officeId: z.string(),
  meetingType: MeetingTypeSchema,
  recaptcha: z.string(),
  timeSlot: z.object({
    end: zDate,
    start: zDate,
    employeeIds: z.array(z.string()),
    timeZone: z.string(),
  }),
  location: z
    .object({
      placeId: z.string().nullable().default(null),
      source: z.enum(["config", "listing", "user"]).default("user"),
    })
    .default({ placeId: null }),
  metadata: z.record(z.unknown()).default({}),
});

export function validateAppointmentFormData(state: PluginState) {
  const payload = {
    subjectId: state.subjectId,
    officeId: state.officeId,
    meetingType: state.meetingType,
    timeSlot: state.timetable,
    recaptcha: state.recaptcha,
    location: state.location,
    metadata: state.metadata,
  };

  const validation = appointmentValidationSchema.safeParse(payload);

  if (!validation.success) {
    throw json(
      ...errors.validation.create({
        translation: { key: "errors.generic" },
        errors: validation.error,
        source: "validateAppointmentFormData",
        extra: payload,
      }),
    );
  }

  return validation.data;
}

async function resolveAppointmentDetails({
  data: { location, meetingType, metadata, officeId, recaptcha, subjectId, timeSlot },
  questionsPromise,
  stateManager,
}: {
  stateManager: ServerStateManager;
  data: ReturnType<typeof validateAppointmentFormData>;
  questionsPromise: Promise<SubjectQuestion[]>;
}) {
  const listing = await getListing({
    config: stateManager.getRequestConfig(),
    params: stateManager.state.config.listing,
  });

  const [answers, customer, appointmentLocation] = await Promise.all([
    resolveSubjectQuestionAnswers({ stateManager, questionsPromise }),
    resolveNewCustomerData(stateManager),
    resolveLocation({ location, meetingType, config: stateManager.getRequestConfig(), listing }),
  ]);

  return {
    subject_id: subjectId,
    office_id: officeId,
    meeting_type: meetingType,
    initiated_from: "Skedify_Plugin",
    listing_uuid: listing?.id ?? null,
    answers,
    customer,
    possibilities: [
      {
        contacts: timeSlot.employeeIds,
        start: formatISOWithoutMs(timeSlot.start),
        end: formatISOWithoutMs(timeSlot.end),
        created_by_type: "customer",
      },
    ],
    recaptcha,
    metadata: metadata ?? "{}",
    location: appointmentLocation,
    initiated_by_type: "customer",
  };
}

async function resolveSubjectQuestionAnswers({
  questionsPromise,
  stateManager,
}: {
  stateManager: ServerStateManager;
  questionsPromise: Promise<SubjectQuestion[]>;
}) {
  const questions = await questionsPromise;

  return parseSubjectQuestionsFormData({ questions, stateManager });
}

function handleTimetableErrorResponse(rawResponse: unknown) {
  const response = APIErrorResponseSchema.parse(rawResponse);

  if (response.status === 409) {
    const hasTimetableError = response.errors.some((error) => timetableErrors.includes(error.code));

    if (hasTimetableError) {
      return;
    }
  }

  throw json(...errors.api.create({ url: response.url, error: rawResponse, extra: response }));
}

async function resolveLocation({
  config,
  listing,
  location,
  meetingType,
}: {
  location: ReturnType<typeof validateAppointmentFormData>["location"];
  meetingType: MeetingType;
  config: RequestConfig;
  listing: SchedulableListing | null;
}): Promise<Partial<Location> | null> {
  if (meetingType !== "on_location") return null;

  if (location.source === "listing") {
    if (!listing?.location) {
      throwNotFoundError({
        translation: { key: "errors.configuration.missingLocation.label" },
      });
    }

    return {
      // geolocation: placeDetails.geolocation,
      city: listing.location.city,
      country: listing.location.country,
      postal_code: listing.location.postalCode,
      state: listing.location.state,
      street_1: listing.location.street1,
      street_2: listing.location.street2,
    };
  }

  if (location.placeId === null) {
    throwNotFoundError({
      translation: { key: "errors.configuration.missingLocation.label" },
    });
  }

  const placeDetails = await getPlaceDetailsById({ place_id: location.placeId, config });

  if (!placeDetails) {
    throwNotFoundError({
      translation: { key: "errors.configuration.missingLocation.label" },
    });
  }

  if (location.source === "user" && !placeDetails.isCompletePlaceDetail) {
    throwNotFoundError({
      translation: { key: "errors.configuration.incompleteLocation.label" },
    });
  }

  return {
    geolocation: placeDetails.geolocation.replace(",", ";"),
    city: placeDetails.city,
    country: placeDetails.country,
    // "fix" for backend requiring a postal code. Some partial addresses dont have a postal code
    postal_code: placeDetails.postalCode || SPACE,
    state: placeDetails.state,
    street_1: [placeDetails.street, placeDetails.streetNumber, placeDetails.subpremise]
      .filter(Boolean)
      .join(" "),
  };
}

// "fix" for backend requiring a postal code. Some partial addresses dont have a postal code
// zero width no-break space
const SPACE = "\uFEFF";
